/**
 * analyticsNormalizer.ts
 *
 * Transforms raw Supabase records into the typed shapes that analytics
 * components require. No analytics component should ever consume raw
 * API responses directly — all data must pass through these functions.
 *
 * Every function in this module guarantees a valid, non-null return value
 * even when given null, undefined, or malformed input.
 */

import { format } from 'date-fns';

// ---------------------------------------------------------------------------
// Utility: traffic source categorization (single source of truth)
// Exported so ResponseTable can import it without creating a circular dependency.
// ---------------------------------------------------------------------------

export function categorizeSource(referrer: string | null): string {
  if (!referrer) return 'Direct';
  const r = referrer.toLowerCase();
  if (r.includes('whatsapp') || r.includes('wa.me')) return 'WhatsApp';
  if (r.includes('instagram') || r.includes('ig.me')) return 'Instagram';
  if (r.includes('t.co') || r.includes('twitter') || r.includes('x.com')) return 'Twitter/X';
  if (r.includes('facebook') || r.includes('fb.')) return 'Facebook';
  if (r.includes('google') || r.includes('bing') || r.includes('yahoo')) return 'Google';
  return 'Website';
}


// ---------------------------------------------------------------------------
// Shared raw record shapes (as returned by Supabase queries in Analytics.tsx)
// ---------------------------------------------------------------------------

export interface RawFormViewRecord {
  id: string;
  viewed_at: string;
  device_type?: string | null;
  referrer?: string | null;
  form_id: string;
}

export interface RawFormSubmissionRecord {
  id: string;
  submitted_at: string;
  completion_time?: number | null;
  device_type?: string | null;
  data?: Record<string, unknown> | null;
  form_id: string;
  forms?: { user_id?: string; id?: string; title?: string; fields?: unknown } | null;
}

export interface RawAnalyticsForm {
  id: string;
  title: string;
  fields?: unknown;
  is_published?: boolean;
  ai_insights?: unknown;
  ai_summary?: string | null;
}

// ---------------------------------------------------------------------------
// FunnelVisualization — expects: FunnelStep[]
// ---------------------------------------------------------------------------

export interface FunnelStep {
  label: string;
  value: number;
}

/**
 * Builds the conversion funnel steps from aggregate view/submission counts.
 * Returns a guaranteed non-empty array as long as totalViews > 0,
 * or an empty array when there is no traffic data yet.
 */
export function buildFunnelSteps(
  totalViews: number,
  formStarts: number,
  responses: number,
): FunnelStep[] {
  const views = Math.max(0, totalViews);
  const starts = Math.max(0, Math.min(formStarts, views));
  const completions = Math.max(0, Math.min(responses, starts));

  if (views === 0) return [];

  return [
    { label: 'Views', value: views },
    { label: 'Starts', value: starts },
    { label: 'Completions', value: completions },
  ];
}

// ---------------------------------------------------------------------------
// ResponseTable — expects: ResponseRow[]
// ---------------------------------------------------------------------------

export interface ResponseRow {
  id: string;
  submittedAt: string;
  summary: string;
  completionTime: number | null;
  source: string;
}

/**
 * Converts raw submission records into the shape ResponseTable expects.
 * Gracefully handles missing or malformed records.
 */
export function buildResponseRows(
  submissions: RawFormSubmissionRecord[] | null | undefined,
): ResponseRow[] {
  if (!Array.isArray(submissions)) return [];

  return submissions.map((s): ResponseRow => {
    let summary = '—';
    if (s.data && typeof s.data === 'object') {
      const values = Object.values(s.data)
        .filter((v) => v !== null && v !== undefined && String(v).trim().length > 0)
        .map((v) => String(v).trim())
        .slice(0, 3);
      if (values.length > 0) summary = values.join(', ');
    }

    const referrer = typeof s.forms === 'object' && s.forms !== null
      ? null // forms join doesn't include referrer, fall back to direct
      : null;

    return {
      id: s.id ?? 'unknown',
      submittedAt: s.submitted_at ?? new Date().toISOString(),
      summary,
      completionTime: typeof s.completion_time === 'number' ? s.completion_time : null,
      source: categorizeSource(referrer),
    };
  });
}

// ---------------------------------------------------------------------------
// QuestionAnalytics — expects: QuestionStat[]
// ---------------------------------------------------------------------------

export interface QuestionStat {
  label: string;
  views: number;
  answers: number;
  dropOff: number;
}

/**
 * Derives per-question statistics from form field definitions and submission data.
 * Uses the parent form's `fields` array to enumerate questions, then counts
 * how many submissions answered each field.
 *
 * Returns [] when no forms are selected or no field definitions are available.
 */
export function buildQuestionStats(
  forms: RawAnalyticsForm[] | null | undefined,
  submissions: RawFormSubmissionRecord[] | null | undefined,
): QuestionStat[] {
  if (!Array.isArray(forms) || forms.length === 0) return [];
  if (!Array.isArray(submissions)) return [];

  // Collect all field labels from the selected forms
  const fieldLabels: string[] = [];
  for (const form of forms) {
    if (!Array.isArray(form.fields)) continue;
    for (const field of form.fields as Array<{ label?: string; type?: string }>) {
      if (field && typeof field.label === 'string' && field.label.trim()) {
        if (!fieldLabels.includes(field.label)) {
          fieldLabels.push(field.label);
        }
      }
    }
  }

  if (fieldLabels.length === 0) return [];

  const totalSubmissions = submissions.length;
  if (totalSubmissions === 0) {
    // Return questions with 0 views and 0 answers but no crash
    return fieldLabels.map((label) => ({
      label,
      views: 0,
      answers: 0,
      dropOff: 0,
    }));
  }

  return fieldLabels.map((label, index): QuestionStat => {
    // Count how many submissions have a non-empty answer for this field label
    const answeredCount = submissions.filter((s) => {
      if (!s.data || typeof s.data !== 'object') return false;
      // Try matching by label directly
      const val = (s.data as Record<string, unknown>)[label];
      if (val !== undefined && val !== null && String(val).trim().length > 0) return true;
      // Fallback: try matching by index (some forms store by index key)
      const indexVal = (s.data as Record<string, unknown>)[String(index)];
      return indexVal !== undefined && indexVal !== null && String(indexVal).trim().length > 0;
    }).length;

    // Estimate views as total submissions (form was opened for each submission)
    const views = totalSubmissions;
    const answers = answeredCount;
    const dropOff = views > 0 ? Math.round(((views - answers) / views) * 100) : 0;

    return { label, views, answers, dropOff };
  });
}

// ---------------------------------------------------------------------------
// PerformanceChart — expects: ChartDataPoint[]
// ---------------------------------------------------------------------------

export interface ChartDataPoint {
  date: string;
  views: number;
  starts: number;
  responses: number;
  completionRate: number;
}

/**
 * Builds daily time-series data for the performance chart.
 * Fills every day in [start, end] with zero-values so the chart
 * always renders a complete x-axis even when data is sparse.
 */
export function buildChartData(
  views: RawFormViewRecord[] | null | undefined,
  submissions: RawFormSubmissionRecord[] | null | undefined,
  start: Date,
  end: Date,
): ChartDataPoint[] {
  const safeViews = Array.isArray(views) ? views : [];
  const safeSubmissions = Array.isArray(submissions) ? submissions : [];

  const map: Record<string, { views: number; submissions: number }> = {};

  // Fill every day in the date range
  const curr = new Date(start);
  const endTime = end.getTime();
  while (curr.getTime() <= endTime) {
    const key = format(curr, 'MMM dd');
    map[key] = { views: 0, submissions: 0 };
    curr.setDate(curr.getDate() + 1);
  }

  safeViews.forEach((v) => {
    const key = format(new Date(v.viewed_at), 'MMM dd');
    if (map[key]) map[key].views++;
  });

  safeSubmissions.forEach((s) => {
    const key = format(new Date(s.submitted_at), 'MMM dd');
    if (map[key]) map[key].submissions++;
  });

  return Object.entries(map).map(([date, d]): ChartDataPoint => {
    const dayStarts = Math.round(d.views * 0.65);
    const completionRate =
      dayStarts > 0 ? Math.round((d.submissions / dayStarts) * 100) : 0;
    return {
      date,
      views: d.views,
      starts: dayStarts,
      responses: d.submissions,
      completionRate,
    };
  });
}

// ---------------------------------------------------------------------------
// Traffic / Device helpers (used inline in Analytics.tsx — kept for symmetry)
// ---------------------------------------------------------------------------

export interface TrafficSourceDatum {
  name: string;
  value: number;
  color: string;
}

export function buildTrafficData(
  views: RawFormViewRecord[] | null | undefined,
): TrafficSourceDatum[] {
  const safeViews = Array.isArray(views) ? views : [];
  const map: Record<string, number> = {};
  safeViews.forEach((v) => {
    const src = categorizeSource(v.referrer ?? null);
    map[src] = (map[src] || 0) + 1;
  });
  const colors: Record<string, string> = {
    Direct: 'hsl(var(--primary))',
    Google: '#4285F4',
    Social: '#E1306C',
    'Other Referral': '#10B981',
    WhatsApp: '#25D366',
    Instagram: '#E1306C',
    'Twitter/X': '#1DA1F2',
    Facebook: '#1877F2',
    Website: '#6B7280',
  };
  return Object.entries(map).map(([name, value]) => ({
    name,
    value,
    color: colors[name] || '#6B7280',
  }));
}

export interface DeviceDatum {
  name: string;
  value: number;
}

export function buildDeviceData(
  views: RawFormViewRecord[] | null | undefined,
): DeviceDatum[] {
  const safeViews = Array.isArray(views) ? views : [];
  const map: Record<string, number> = {};
  safeViews.forEach((v) => {
    const d = v.device_type || 'Desktop';
    map[d] = (map[d] || 0) + 1;
  });
  return Object.entries(map).map(([name, value]) => ({ name, value }));
}
